import { useState } from 'react';
import { Scene } from '@/content/scenes';
import { PIP_AND_THE_PAPER_MOON, PLANNED_COLLECTIONS } from '@/content/stories';
import { TemplateThumb } from './TrySample';
import { Button } from '@/design-system/Button';
import { Card, Chip, SectionHeading, TornCard } from '@/design-system/Surfaces';
import { SparkleField, SparkleGlyph } from '@/design-system/Sparkle';
import { cn } from '@/design-system/cn';
import { IconArrow, IconBook, IconPalette, IconSpeaker } from '@/features/studio/Icons';
import { CAPABILITIES } from '@/contracts/brand';

/**
 * Marketing home (spec §5 W01), in the specified top-to-bottom order.
 *
 * Everything claimed here is something the product actually does today.
 * There are no invented testimonials, award badges, user counts or learning
 * percentages (spec §5 W02–W05), and no price is shown before products are
 * configured (spec §5 W01 item 10).
 */
export function Home() {
  return (
    <>
      <Hero />
      <ExperienceStrip />
      <SignatureDemo />
      <ParentValue />
      <Collections />
      <GalleryPreview />
      <FamilyStory />
      <TrustSummary />
      <PricingPreview />
      <Faq />
      <FinalCta />
    </>
  );
}

/* ----------------------------- 2. Hero ----------------------------- */

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div aria-hidden className="absolute inset-0" style={{ background: 'var(--gradient-dawn)' }} />
      <SparkleField count={16} seed={5} />
      <div className="u-shell relative grid items-center gap-2xl py-3xl lg:grid-cols-2 lg:py-[88px]">
        <div className="animate-slide-up">
          <Chip tone="magic" icon={<SparkleGlyph className="h-3.5 w-3.5" />}>
            For 3–7 year olds and the grown-up next to them
          </Chip>
          <h1 className="mt-l font-display text-[40px] font-semibold leading-[1.05] text-ink sm:text-[56px] lg:text-[62px]">
            Read a story.
            <br />
            <span className="u-gradient-text">Make it yours.</span>
          </h1>
          <p className="mt-l max-w-lg text-[19px] leading-relaxed text-ink-soft">
            Children listen or read an illustrated story, think their way through a short game,
            and colour a picture from it. Then their own drawing moves — inside the story it came
            from.
          </p>
          <div className="mt-2xl flex flex-wrap gap-m">
            <Button variant="magic" size="xl" to="/try" iconRight={<IconArrow />}>
              Try a story
            </Button>
            <Button variant="outline" size="xl" to="/how-it-works">
              See how it works
            </Button>
          </div>
          <p className="mt-l text-[15px] text-ink-faint">
            The full sample is free, needs no account, and stays on your device.
          </p>
        </div>

        <HeroShowcase />
      </div>
    </section>
  );
}

/**
 * The hero shows the real transformation: the story scene, the actual line
 * art from the product, and a coloured result. Sample art is labelled as
 * sample art (spec §5 W01 item 4).
 */
function HeroShowcase() {
  const tpl = PIP_AND_THE_PAPER_MOON.currentVersion.templates[1];
  return (
    <div className="relative animate-pop-in u-stack-2">
      <div className="overflow-hidden rounded-blob border-[3px] border-line shadow-cut-lg">
        <Scene id="pip-p6-paper-moon" className="aspect-[3/2] w-full" title="A glowing paper moon over a moonlit pond" />
      </div>

      <div className="absolute -bottom-8 -left-4 w-[42%] max-w-[210px] rotate-[-7deg] rounded-blob border-[3px] border-line bg-white p-m shadow-cut-lg sm:-left-8">
        <div className="aspect-square w-full">
          <TemplateThumb templateId={tpl.id} />
        </div>
        <p className="mt-xs text-center font-display text-[12px] font-semibold uppercase tracking-wide text-ink-faint">
          The blank picture
        </p>
      </div>

      <div className="absolute -right-2 -top-8 w-[36%] max-w-[178px] rotate-[8deg] rounded-blob border-[3px] border-line bg-white p-m shadow-cut-lg sm:-right-6">
        <div className="aspect-square w-full">
          <ColoredSwayExample />
        </div>
        <p className="mt-xs text-center font-display text-[12px] font-semibold uppercase tracking-wide text-ink-faint">
          Sample colouring
        </p>
      </div>
    </div>
  );
}

/** An illustrative colouring of the real template — labelled as a sample. */
function ColoredSwayExample() {
  const tpl = PIP_AND_THE_PAPER_MOON.currentVersion.templates[1];
  const sample: Record<string, string> = {
    halo: '#FFF1BF',
    'wing-upper-l': '#20B2BE',
    'wing-upper-r': '#20B2BE',
    'wing-lower-l': '#7C4FE8',
    'wing-lower-r': '#7C4FE8',
    'spot-l': '#FFC94D',
    'spot-r': '#FFC94D',
    body: '#2C1C38',
    head: '#2C1C38',
  };
  return (
    <svg viewBox={`0 0 ${tpl.width} ${tpl.height}`} className="h-full w-full" aria-hidden>
      {tpl.regions.map((r) =>
        sample[r.id] ? <path key={r.id} d={r.d} fill={sample[r.id]} /> : null,
      )}
      <path d={tpl.outline} fill="none" stroke="#2C1C38" strokeWidth="4" strokeLinejoin="round" strokeLinecap="round" />
      {tpl.detail ? <path d={tpl.detail} fill="#2C1C38" /> : null}
    </svg>
  );
}

/* ------------------------ 3. Experience strip ------------------------ */

const STEPS = [
  {
    icon: <IconSpeaker />,
    title: 'Read or listen',
    body: 'Three ways in: listen together, read with help, or read it yourself.',
    tint: 'accent' as const,
  },
  {
    icon: <IconBook />,
    title: 'Explore',
    body: 'One short thinking game. No score, no pass mark, and help is always there.',
    tint: 'sunny' as const,
  },
  {
    icon: <IconPalette />,
    title: 'Create',
    body: 'Three pictures from the story, ready to colour. Markers, crayons, paint, glitter — and marks that stay inside the lines.',
    tint: 'secondary' as const,
  },
  {
    icon: <SparkleGlyph className="h-[22px] w-[22px]" />,
    title: 'Bring it to life',
    body: 'Their drawing moves in the story — their brush marks, their colours.',
    tint: 'primary' as const,
  },
];

function ExperienceStrip() {
  return (
    <section className="u-shell py-3xl">
      <SectionHeading
        align="center"
        eyebrow="The loop"
        title="Four steps, every single time"
        description="Predictable enough that a four-year-old knows what happens next, without an adult reading the screen for them."
      />
      <ol className="mt-2xl grid gap-l sm:grid-cols-2 lg:grid-cols-4">
        {STEPS.map((s, i) => (
          <li key={s.title}>
            <TornCard tint={s.tint} className={cn('h-full p-xl animate-pop-in', [`u-stack-1`, `u-stack-2`, `u-stack-3`, `u-stack-4`][i])}>
              <div className="mb-m flex h-12 w-12 items-center justify-center rounded-pebble border-2 border-ink/10 bg-card text-ink">
                {s.icon}
              </div>
              <h3 className="font-display text-[19px] font-semibold text-ink">
                <span className="text-ink-faint">{i + 1}. </span>
                {s.title}
              </h3>
              <p className="mt-s text-[16px] leading-relaxed text-ink-soft">{s.body}</p>
            </TornCard>
          </li>
        ))}
      </ol>
    </section>
  );
}

/* --------------------- 4. Signature demonstration --------------------- */

function SignatureDemo() {
  const [tab, setTab] = useState(0);
  const tabs = [
    { label: 'The story scene', node: <Scene id="pip-p3-sway" className="aspect-[3/2] w-full" title="A glowing moth lights the dark above a fox" /> },
    { label: 'The picture to colour', node: <div className="grid aspect-[3/2] place-items-center bg-white p-l"><div className="h-full"><TemplateThumb templateId="tpl_sway_wings" /></div></div> },
    { label: 'What they made', node: <div className="grid aspect-[3/2] place-items-center bg-white p-l"><div className="h-full"><ColoredSwayExample /></div></div> },
  ];

  return (
    <section className="bg-paper-deep/50 py-3xl">
      <div className="u-shell">
        <SectionHeading
          eyebrow="The signature moment"
          title="The same picture, three times over"
          description="This is the actual line art and the actual scene from the free story — not a mock-up. The coloured version is a sample we made to show the idea."
        />
        <div className="mt-xl flex flex-wrap gap-s" role="tablist" aria-label="Transformation stages">
          {tabs.map((t, i) => (
            <button
              key={t.label}
              role="tab"
              aria-selected={tab === i}
              onClick={() => setTab(i)}
              className={cn(
                'min-h-touch rounded-pebble border-2 px-l font-display text-[15px] font-semibold transition',
                tab === i ? 'border-magic bg-magic/10 text-magic-deep' : 'border-line bg-card text-ink-soft',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="mt-l overflow-hidden rounded-blob border-[3px] border-line shadow-cut-lg">
          {tabs[tab].node}
        </div>
      </div>
    </section>
  );
}

/* ------------------------- 5. Parent value ------------------------- */

function ParentValue() {
  const items = [
    { title: 'They make something', body: 'Not a score. A picture that exists afterwards, in their own copy of the story.' },
    { title: 'Reading support that fits', body: 'Switch between listening, reading with help, and reading alone without restarting anything.' },
    { title: 'Keepsakes you keep', body: 'Finished pictures and personal editions stay yours, and stay available.' },
    { title: 'A comfortable ending', body: 'A bedtime mode and an honest “finish it tomorrow” — instead of one more level.' },
  ];
  return (
    <section className="u-shell py-3xl">
      <SectionHeading eyebrow="Why parents stay" title="Built around the end of the day, not around screen time" />
      <div className="mt-2xl grid gap-l sm:grid-cols-2">
        {items.map((it) => (
          <Card key={it.title} className="flex gap-l">
            <SparkleGlyph className="mt-1 h-5 w-5 shrink-0 text-magic" />
            <div>
              <h3 className="font-display text-[18px] font-semibold text-ink">{it.title}</h3>
              <p className="mt-xs text-[16px] leading-relaxed text-ink-soft">{it.body}</p>
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
}

/* ------------------------- 6. Collections ------------------------- */

function Collections() {
  return (
    <section className="u-shell py-3xl">
      <SectionHeading
        eyebrow="Collections"
        title="One story is ready. More are being written."
        description="We would rather tell you exactly what exists than show you a shelf of covers that do not open."
      />
      <div className="mt-2xl grid gap-l sm:grid-cols-2 lg:grid-cols-4">
        {PLANNED_COLLECTIONS.map((c) => (
          <article key={c.id} className="overflow-hidden rounded-blob border-2 border-line bg-card shadow-cut">
            <div className="relative">
              <Scene id={c.sceneId} className="aspect-[4/3] w-full" title={c.title} />
              {c.status === 'planned' ? (
                <span className="absolute inset-0 grid place-items-center bg-ink/55 font-display text-[15px] font-semibold text-white">
                  In the works
                </span>
              ) : null}
            </div>
            <div className="p-l">
              <div className="mb-s flex items-center justify-between gap-s">
                <h3 className="font-display text-[17px] font-semibold text-ink">{c.title}</h3>
                <Chip tone={c.status === 'available' ? 'leaf' : 'neutral'}>
                  {c.status === 'available' ? `${c.storyCount} story` : 'Planned'}
                </Chip>
              </div>
              <p className="text-[15px] leading-relaxed text-ink-soft">{c.description}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

/* --------------------- 7. Gallery preview --------------------- */

function GalleryPreview() {
  // The Magic Gallery is real in the design but needs a backend and staffed
  // moderation before any child's drawing is published. Saying so is better
  // than showing invented artwork with invented nicknames (spec §11.1).
  if (CAPABILITIES.publicGallery) return null;
  return (
    <section className="u-shell py-3xl">
      <Card className="bg-accent-soft/50">
        <div className="flex flex-col gap-l sm:flex-row sm:items-center sm:justify-between">
          <div className="max-w-xl">
            <Chip tone="accent">Not open yet</Chip>
            <h2 className="mt-m font-display text-[24px] font-semibold text-ink">The Magic Gallery</h2>
            <p className="mt-s text-[16px] leading-relaxed text-ink-soft">
              A place where approved drawings are shown with a chosen nickname and nothing else —
              no profiles, no comments, no follower counts, and a “sparkle” instead of a like.
              It stays closed until a parent can approve every picture and a person reviews each
              one. We are not going to fill it with made-up artwork in the meantime.
            </p>
          </div>
          <Button variant="outline" size="md" to="/community-guidelines">
            Read the rules
          </Button>
        </div>
      </Card>
    </section>
  );
}

/* ---------------------- 8. Family story ---------------------- */

function FamilyStory() {
  const modes = [
    { title: 'Listen together', body: 'The story reads itself. You sit next to them. Good for 3s and 4s, and for a tired Tuesday.' },
    { title: 'Read with help', body: 'They read; a tap hears any page again. The help is on the page, not a grown-up leaning over.' },
    { title: 'Read myself', body: 'Narration off, help still one tap away. Nothing is called “advanced”, so no mode feels like the baby one.' },
  ];
  return (
    <section className="bg-paper-deep/50 py-3xl">
      <div className="u-shell">
        <SectionHeading eyebrow="Reading support" title="Three ways in, switchable mid-story" />
        <div className="mt-2xl grid gap-l lg:grid-cols-3">
          {modes.map((m) => (
            <Card key={m.title}>
              <h3 className="font-display text-[18px] font-semibold text-ink">{m.title}</h3>
              <p className="mt-s text-[16px] leading-relaxed text-ink-soft">{m.body}</p>
            </Card>
          ))}
        </div>
        <p className="mt-xl max-w-2xl text-[16px] leading-relaxed text-ink-soft">
          Changing the mode never restarts the story or resets a picture. We do not claim to teach
          a child to read — that needs sound–letter work this product does not do yet. What it does
          is story enjoyment, comprehension practice, and making things.
        </p>
      </div>
    </section>
  );
}

/* ---------------------- 9. Trust summary ---------------------- */

function TrustSummary() {
  const claims = [
    'No advertising, and no third-party ad or tracking SDKs.',
    'No comments, no messages, no following, no public child profiles.',
    'Nothing a child makes becomes public without a parent approving that exact picture.',
    'The free sample runs entirely on your device and uploads nothing.',
  ];
  return (
    <section className="u-shell py-3xl">
      <SectionHeading eyebrow="What we will and will not do" title="Only the promises we actually keep" />
      <ul className="mt-xl grid gap-m sm:grid-cols-2">
        {claims.map((c) => (
          <li key={c} className="flex gap-m rounded-pebble border-2 border-line bg-card p-l">
            <SparkleGlyph className="mt-[3px] h-4 w-4 shrink-0 text-leaf" />
            <span className="text-[16px] leading-relaxed text-ink-soft">{c}</span>
          </li>
        ))}
      </ul>
      <p className="mt-l text-[15px] text-ink-faint">
        Cloud sync, the gallery and purchases are still being built. Where a control is not real
        yet, the app says so instead of showing a button that does nothing.
      </p>
    </section>
  );
}

/* ---------------------- 10. Pricing preview ---------------------- */

function PricingPreview() {
  return (
    <section className="u-shell py-3xl">
      <div className="grid gap-l lg:grid-cols-2">
        <Card className="bg-primary-soft/50">
          <Chip tone="leaf">Available now</Chip>
          <h3 className="mt-m font-display text-[24px] font-semibold text-ink">The free starter</h3>
          <p className="mt-s text-[16px] leading-relaxed text-ink-soft">
            The complete flagship story, its activities, all three pictures, the drawing studio and
            the living scene. Not a trial and not a teaser — the whole loop.
          </p>
          <Button variant="magic" size="lg" to="/try" className="mt-l">
            Start it now
          </Button>
        </Card>
        <Card>
          <Chip tone="neutral">Not for sale yet</Chip>
          <h3 className="mt-m font-display text-[24px] font-semibold text-ink">Family membership</h3>
          <p className="mt-s text-[16px] leading-relaxed text-ink-soft">
            More reviewed collections as they are finished. There is no price on this page because
            the products are not configured, and we are not going to invent one. Finished pictures
            and completed keepsakes stay available even if a membership ends.
          </p>
          <Button variant="outline" size="lg" to="/pricing" className="mt-l">
            What is planned
          </Button>
        </Card>
      </div>
    </section>
  );
}

/* ---------------------------- 11. FAQ ---------------------------- */

const FAQ = [
  {
    q: 'What ages is it for?',
    a: 'Roughly 3 to 7, with most attention on 4 to 6. Which reading mode suits a child is a parent’s call, not a test result.',
  },
  {
    q: 'Is there an iPhone or iPad app?',
    a: 'It is being built as a real universal app rather than a wrapped website. There is no App Store listing yet, and this site will not show a badge until there is one.',
  },
  {
    q: 'Does it work without internet?',
    a: 'The drawing studio and any downloaded story do. The gallery does not, because whether a picture is still approved can change.',
  },
  {
    q: 'Can other people see my child’s drawings?',
    a: 'Only if a parent approves that exact picture and a reviewer approves it too. Until then it is private, and a parent can withdraw it later.',
  },
  {
    q: 'What happens if we stop paying?',
    a: 'Finished pictures and completed keepsake editions stay available under the published retention terms. New premium stories stop.',
  },
  {
    q: 'What do you collect about my child?',
    a: 'A nickname, a preset avatar and a broad age band. No full name, birth date, photo, school or location.',
  },
];

function Faq() {
  return (
    <section className="bg-paper-deep/50 py-3xl">
      <div className="u-shell">
        <SectionHeading eyebrow="Questions" title="The ones parents actually ask" />
        <div className="mt-xl grid gap-m lg:grid-cols-2">
          {FAQ.map((f) => (
            <details key={f.q} className="group rounded-blob border-2 border-line bg-card p-l shadow-press">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-m font-display text-[17px] font-semibold text-ink">
                {f.q}
                <span aria-hidden className="text-magic transition group-open:rotate-45">+</span>
              </summary>
              <p className="mt-m text-[16px] leading-relaxed text-ink-soft">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------- 12. Final CTA -------------------------- */

function FinalCta() {
  return (
    <section className="relative overflow-hidden py-3xl">
      <div aria-hidden className="absolute inset-0 opacity-90" style={{ background: 'var(--gradient-dawn)' }} />
      <SparkleField count={14} seed={19} />
      <div className="u-shell relative text-center">
        <h2 className="mx-auto max-w-2xl font-display text-[32px] font-semibold leading-tight text-ink sm:text-[42px]">
          Five minutes, one story, and something they made.
        </h2>
        <Button variant="magic" size="xl" to="/try" className="mt-xl" iconRight={<IconArrow />}>
          Try a story
        </Button>
      </div>
    </section>
  );
}
